import { memo, useCallback } from 'react'
import { Box, Button, Slider, Typography } from '@mui/material'

const SLIDER_SX = {
    '& .MuiSlider-thumb': {
        width: 20,
        height: 20,
        backgroundColor: 'primary.main',
        border: '2px solid currentColor',
        '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)',
        },
    },
    '& .MuiSlider-valueLabel': {
        fontSize: 12,
        fontWeight: 500,
        top: -6,
        padding: '2px 4px',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 2,
        '&:before': { display: 'none' },
        '& *': { color: 'inherit' },
    },
    '& .MuiSlider-track': { height: 4, border: 'none' },
    '& .MuiSlider-rail': { height: 4, opacity: 0.3 },
} as const

export const OI_TIME_WINDOW_MINUTES = [5, 10, 15, 30, 60, 120, 180] as const

export const oiTimeWindowLabel = (mins: number) => {
    if (mins === 60) return 'Last 1 Hr'
    if (mins === 120) return 'Last 2 Hrs'
    if (mins === 180) return 'Last 3 Hrs'
    return `Last ${mins} mins`
}

export type OiAnalysisTimeSliderProps = {
    loading: boolean
    minuteMap: number[]
    value: [number, number]
    onSliderChange: (_: unknown, v: number | number[]) => void
    onSliderCommit: (_: unknown, v: number | number[]) => void
    onQuickSelect: (minutes: number | 'full') => void
    compact?: boolean
}

const OiAnalysisTimeSlider = ({
    loading,
    minuteMap,
    value,
    onSliderChange,
    onSliderCommit,
    onQuickSelect,
    compact = false,
}: OiAnalysisTimeSliderProps) => {
    const maxIndex = minuteMap.length - 1
    const startLabel = minuteMap.length > 0 ? formatMinuteLabel(minuteMap[0]) : ''
    const endLabel = minuteMap.length > 0 ? formatMinuteLabel(minuteMap[maxIndex]) : ''

    const valueLabelFormat = useCallback(
        (i: number) => formatMinuteLabel(minuteMap[i]),
        [minuteMap]
    )

    return (
        <>
            <Box sx={{ px: compact ? 1 : 2, mb: compact ? 1 : 2, display: 'flex', alignItems: 'center', gap: compact ? 1 : 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px', textAlign: 'left' }}>
                    {startLabel}
                </Typography>
                <Box sx={{ flex: 1, display: 'flex' }}>
                    <Slider
                        value={value}
                        min={0}
                        max={maxIndex}
                        disabled={loading}
                        step={1}
                        onChange={onSliderChange}
                        onChangeCommitted={onSliderCommit}
                        valueLabelDisplay="on"
                        valueLabelFormat={valueLabelFormat}
                        sx={SLIDER_SX}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px', textAlign: 'right' }}>
                    {endLabel}
                </Typography>
            </Box>
            <Box sx={{ px: compact ? 1 : 2, mb: compact ? 1 : 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {OI_TIME_WINDOW_MINUTES.map((mins) => (
                    <Button
                        key={mins}
                        variant="outlined"
                        size="small"
                        onClick={() => onQuickSelect(mins)}
                        disabled={loading}
                    >
                        {oiTimeWindowLabel(mins)}
                    </Button>
                ))}
                <Button variant="outlined" size="small" onClick={() => onQuickSelect('full')} disabled={loading}>
                    Full Day
                </Button>
            </Box>
        </>
    )
}

function formatMinuteLabel(timestamp: number) {
    return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
}

export default memo(OiAnalysisTimeSlider)
